import React, { useState, useEffect } from 'react';
import PageLoader from '../components/PageLoader';
import FeedbackModal from '../components/FeedbackModal';
import { api } from '../App';
import '../styles/AdminBestellingenBySpeltakPage.css';

function AdminBestellingenBySpeltakPage({ user, speltak_id, speltak_naam, onBack, onLogout, onViewOrder }) {
  const [bestellingen, setBestellingen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [closingId, setClosingId] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [confirmClose, setConfirmClose] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchBestellingen = async () => {
      try {
        setError('');
        const response = await api.get(`/index.php?action=adminbestellingen&speltak_id=${speltak_id}`);
        if (cancelled) return;
        setBestellingen(response.data);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError('Fout bij laden van bestellingen');
        setLoading(false);
      }
    };

    fetchBestellingen();
    return () => {
      cancelled = true;
    };
  }, [speltak_id]);

  const handleCloseOrder = async (orderId) => {
    try {
      setClosingId(orderId);
      setError('');
      await api.post('/index.php?action=sluitbestelling', { bstlid: orderId });
      setBestellingen((prev) =>
        prev.map((order) =>
          order.ID === orderId ? { ...order, status: 1 } : order
        )
      );
      setConfirmClose(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Fout bij sluiten van bestelling');
    } finally {
      setClosingId(null);
    }
  };

  const handleOpenOrder = async (orderId) => {
    try {
      setOpeningId(orderId);
      setError('');
      await api.post('/index.php?action=heropenbestelling', { bstlid: orderId });
      setBestellingen((prev) =>
        prev.map((order) =>
          order.ID === orderId ? { ...order, status: 0 } : order
        )
      );
      setConfirmOpen(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Fout bij heropenen van bestelling');
    } finally {
      setOpeningId(null);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="admin-bestellingen-speltak-container">
      {confirmOpen && (
        <FeedbackModal
          title="Bestelling heropenen?"
          message={`Weet je zeker dat je bestelling #${confirmOpen} wilt heropenen? De speltak kan de bestelling dan weer wijzigen.`}
          onConfirm={() => handleOpenOrder(confirmOpen)}
          onCancel={() => setConfirmOpen(null)}
          confirmLabel="Heropenen"
          cancelLabel="Annuleren"
        />
      )}

      {confirmClose && (
        <FeedbackModal
          title="Bestelling sluiten?"
          message={`Weet je zeker dat je bestelling #${confirmClose} wilt sluiten? De speltak kan deze bestelling daarna niet meer wijzigen.`}
          onConfirm={() => handleCloseOrder(confirmClose)}
          onCancel={() => setConfirmClose(null)}
          confirmLabel="Sluiten"
          cancelLabel="Annuleren"
        />
      )}

      <header className="admin-header">
        <button onClick={onBack} className="back-button">← Terug</button>
        <h1>Bestellingen - {speltak_naam}</h1>
        <button onClick={onLogout} className="logout-button">Uitloggen</button>
      </header>

      <main className="admin-main">
        <div className="admin-content">
          {error && <div className="error-message">{error}</div>}

          {bestellingen.length === 0 ? (
            <div className="no-orders">
              <p>Geen bestellingen gevonden voor {speltak_naam}</p>
            </div>
          ) : (
            <table className="bestellingen-table">
              <thead>
                <tr>
                  <th>Bestel ID</th>
                  <th>Besteldatum</th>
                  <th>Leverdatum</th>
                  <th>Status</th>
                  <th>Acties</th>
                </tr>
              </thead>
              <tbody>
                {bestellingen.map((order) => (
                  <tr key={order.ID}>
                    <td>#{order.ID}</td>
                    <td>{order.besteldatum}</td>
                    <td>{order.leverdatum}</td>
                    <td>
                      <span className={`status status-${order.status === 0 ? 'open' : 'gesloten'}`}>
                        {order.status === 0 ? 'Open' : 'Gesloten'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button
                        type="button"
                        className="view-button"
                        onClick={() => onViewOrder(order.ID, speltak_naam)}
                      >
                        Bekijk
                      </button>
                      {order.status === 0 && (
                        <button
                          type="button"
                          className="close-button"
                          onClick={() => setConfirmClose(order.ID)}
                          disabled={closingId === order.ID}
                        >
                          {closingId === order.ID ? 'Sluiten...' : 'Sluiten'}
                        </button>
                      )}
                      {order.status === 1 && (
                        <button
                          type="button"
                          className="open-button"
                          onClick={() => setConfirmOpen(order.ID)}
                          disabled={openingId === order.ID}
                        >
                          {openingId === order.ID ? 'Heropenen...' : 'Heropenen'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}

export default AdminBestellingenBySpeltakPage;
